/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import type { TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { type NounKeys } from '../../../../constants/nouns';
import { ProductKeys3P, type ProductKeys } from '../../../../constants/products';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../../../utils/experiments';
import { IconDecider } from '../../../product-icon';
import FilterMenu from '../filter-menu';
import { FilterOptionTimeout } from '../filter-option-timeout';
import { messages } from '../messages';
import { ResultCount } from '../result-count';
import { ResultCountLoading } from '../result-count-loading';

const baseStyles = xcss({
	height: '36px',
	paddingInline: 'space.150',
	width: '100%',
	color: 'color.text',
	background: 'none',
});

// Single-select variant base styles
const baseStylesSingleSelect = xcss({
	height: 'space.400', // 32px
	paddingBlock: 'space.075',
	paddingInline: 'space.100',
	width: '100%',
	color: 'color.text',
	background: 'none',
	display: 'flex',
	alignItems: 'center',
	borderRadius: 'radius.small',

	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},
});

const selectedStyles = xcss({
	color: 'color.text.accent.blue',
	background: token('color.background.selected'),
	':hover': {
		color: 'color.text.accent.blue',
		background: token('color.background.selected'),
	},
});

// Single-select variant selected styles — left side is square so the inset left border doesn't curve,
// right side keeps the border radius to match the hover state.
const selectedStylesSingleSelect = xcss({
	color: 'color.text.accent.blue',
	background: token('color.background.selected'),
	borderStartStartRadius: 0,
	borderEndStartRadius: 0,
	borderStartEndRadius: 'radius.small',
	borderEndEndRadius: 'radius.small',
	':hover': {
		color: 'color.text.accent.blue',
		background: token('color.background.selected'),
	},
});

// NOTE: When cleaning up rovo_filters_discoverability FG, add borderLeft attribute to selectedStyles above
const selectedStyleBorder = xcss({
	// @ts-ignore
	// - Don't want to use borderLeft because it takes up space
	// - Can't use outline because we can't only target the left side to have the outline
	// - We'd rather ignore the TS error than add a new div with margin or position hacks
	boxShadow: `inset ${token('space.025')} 0px ${token('color.border.selected')}`,
});

const containerStyles = xcss({
	position: 'relative',
	paddingBottom: 'space.025',
	width: '100%',
	':hover': {
		color: 'color.text',
		background: token('color.background.neutral.subtle.hovered'),
	},
});

// Single-select variant container styles
const containerStylesSingleSelect = xcss({
	position: 'relative',
	paddingBottom: 'space.025',
	width: '100%',
	background: 'none',
	':hover': {
		color: 'color.text',
		background: 'none',
	},
});

const containerStylesZeroCount = xcss({
	opacity: 0.5,
});

const inlineStyles = xcss({
	marginLeft: 'space.075',
});

// Single-select variant inline styles with 6px (space.050) gap
const inlineStylesSingleSelect = xcss({
	marginLeft: 'space.050',
});

// Spacer to reserve space for the FilterMenu when it's visible in the overlay.
// This maintains the same label truncation as when FilterMenu was inside the grid.
// Matches the visible FilterMenu: 32px (compact IconButton) + 8px marginLeft.
const filterMenuSpacerStyles = xcss({
	width: '32px',
	marginLeft: 'space.100',
	height: '0px',
});

const interactiveOverlayStyles = xcss({
	position: 'absolute',
	right: 'space.150',
	// Adjust for paddingBottom on container to align with Pressable content center
	top: 'calc(50% - 1px)',
	transform: 'translateY(-50%)',
	display: 'flex',
	alignItems: 'center',
});

// Single-select variant — flush to the right edge so the IconButton's internal padding
// naturally aligns the icon to match the left paddingInline (space.100 = 8px)
const interactiveOverlayStylesSingleSelect = xcss({
	position: 'absolute',
	right: 'space.100',
	// Adjust for paddingBottom on container to align with Pressable content center
	top: 'calc(50% - 1px)',
	transform: 'translateY(-50%)',
	display: 'flex',
	alignItems: 'center',
});

interface Option {
	label: React.ReactNode;
	value: ProductKeys | NounKeys | undefined;
	/**
	 * Number of results available in the index if a search is done with this option selected.
	 */
	count?: number;
	isSelected: boolean;
	isPrimaryProduct?: boolean; // optional since Option is used for non-product options too, eg. All or Nouns
	resultCountLimit?: number;
	countLoading?: boolean;
	timeout?: boolean;
	onSelect?: () => void;
}

const FilterOptionResultCount = ({ option }: { option: Option }): React.JSX.Element => {
	if (
		option.value === ProductKeys3P.Gmail &&
		option.resultCountLimit !== undefined &&
		fg('formula_3p_gmail_result_count')
	) {
		return (
			<ResultCount
				count={option.count}
				isSelected={option.isSelected}
				resultCountLimit={option.resultCountLimit}
			/>
		);
	}

	if (
		option.value === ProductKeys3P.OutlookMail &&
		option.resultCountLimit !== undefined &&
		fg('formula_3p_outlook_result_count')
	) {
		return (
			<ResultCount
				count={option.count}
				isSelected={option.isSelected}
				resultCountLimit={option.resultCountLimit}
			/>
		);
	}

	return <ResultCount count={option.count} isSelected={option.isSelected} />;
};

interface FilterOptionProps {
	option: Option;
	isDisabled?: boolean;
	oAuthWarning?: React.ReactNode;
	triggerProps?: TriggerProps;
	onClickMenuItem?: (ev: React.MouseEvent | React.KeyboardEvent, action: string) => void;
	onClick?: () => void;
	ariaRole?: string;
	isHidden?: boolean;
	isShowMore?: boolean;
	onHover?: () => void;
	onHoverMenuButton?: () => void;
	onHoverMenuItem?: () => void;
}

const labelStyles = cssMap({
	truncated: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		textWrap: 'nowrap',
		textAlign: 'left',
		flexShrink: 1,
		font: token('font.body'),
	},
});

const gridStyles = xcss({
	width: '100%',
});

// Single-select variant grid styles with 6px gap
const gridStylesSingleSelect = xcss({
	width: '100%',
	gap: 'space.075', // 6px gap between icon, label, and count
});

const testIdGenerator = (value?: string, isShowMore?: boolean) => {
	if (isShowMore) {
		return 'search-page-app-filter-sidebar-show-more';
	}

	if (value) {
		return `filter-${value}`;
	}

	return 'filter-all';
};

const FilterOption = ({
	option,
	isDisabled,
	oAuthWarning = null,
	triggerProps,
	onClickMenuItem,
	onClick,
	ariaRole,
	isHidden,
	isShowMore = false,
	onHover,
	onHoverMenuButton,
	onHoverMenuItem,
}: FilterOptionProps): JSX.Element => {
	const [isMouseOver, setIsMouseOver] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { formatMessage } = useIntl();
	const isConnector =
		Boolean(option.value) &&
		option.value !== 'goal' &&
		option.value !== 'project' &&
		option.value !== 'atlassian-web-crawler';

	const isMenuTriggerVisible =
		!isHidden && isConnector && (isMouseOver || isMenuOpen) && !(option.isPrimaryProduct || false);

	const noMatchingResults = option?.count === 0;
	const shouldBeGreyedOut = !option.isSelected && noMatchingResults && !isDisabled;
	const labelRef = useRef<HTMLDivElement>(null);
	const canTooltipAppear = useCallback(
		() =>
			noMatchingResults ||
			(!isMenuOpen && (labelRef.current?.offsetWidth ?? 0) < (labelRef.current?.scrollWidth ?? 0)),
		[noMatchingResults, isMenuOpen],
	);
	const tooltipContent = noMatchingResults
		? formatMessage(messages.noMatchingResultsTooltip, { product: option.label })
		: option.label;

	// Determine which interactive element to show in the overlay (outside the button)
	const showTimeoutOverlay =
		!oAuthWarning && !option.countLoading && option.timeout && !option.isSelected;
	const showFilterMenuOverlay =
		!isHidden && !isShowMore && !(option.timeout && !option.isSelected) && !option.countLoading;

	// Show non-interactive content inside the button only when no interactive overlay is visible
	const showResultCount =
		!oAuthWarning &&
		!option.countLoading &&
		!(option.timeout && !option.isSelected) &&
		!isMenuTriggerVisible;

	return (
		<Box
			xcss={[
				getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
					? containerStylesSingleSelect
					: containerStyles,
				shouldBeGreyedOut && containerStylesZeroCount,
			]}
			onMouseEnter={() => {
				setIsMouseOver(true);
				onHover?.();
			}}
			onMouseLeave={() => {
				setIsMouseOver(false);
			}}
			onFocus={() => setIsMouseOver(true)}
			onBlur={() => setIsMouseOver(false)}
			role={ariaRole}
		>
			<Tooltip content={tooltipContent} delay={300} canAppear={canTooltipAppear} hideTooltipOnClick>
				<Pressable
					{...triggerProps}
					key={option.value}
					onClick={isDisabled ? () => {} : onClick}
					xcss={[
						getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
							? baseStylesSingleSelect
							: baseStyles,
						option.isSelected &&
							(getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
								? selectedStylesSingleSelect
								: selectedStyles),
						option.isSelected && selectedStyleBorder,
					]}
					padding="space.0"
					testId={testIdGenerator(option.value, isShowMore)}
					aria-current={option.isSelected}
				>
					<Grid
						templateColumns="auto 1fr auto"
						gap={
							getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
								? 'space.075'
								: 'space.100'
						}
						alignItems="center"
						justifyContent="start"
						xcss={
							getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
								? gridStylesSingleSelect
								: gridStyles
						}
					>
						<IconDecider
							productOrNounKey={option.value}
							isShowMore={isShowMore}
							label=""
							size={
								getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
									? 'xxsmall'
									: 'small'
							}
							isSelected={option.isSelected}
						/>

						<Box as="div" ref={labelRef} xcss={cx(labelStyles.truncated)}>
							<Text
								color={option.isSelected ? 'color.text.accent.blue' : 'color.text'}
								align="start"
							>
								{option.label}
							</Text>
						</Box>

						<Inline
							alignBlock="center"
							xcss={
								getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
									? inlineStylesSingleSelect
									: inlineStyles
							}
						>
							{oAuthWarning ? (
								oAuthWarning
							) : option.countLoading ? (
								<ResultCountLoading />
							) : showResultCount ? (
								<FilterOptionResultCount option={option} />
							) : null}
							{/* Spacer reserves space for FilterMenu when visible in overlay */}
							{isMenuTriggerVisible && <Box xcss={filterMenuSpacerStyles} aria-hidden="true" />}
						</Inline>
					</Grid>
				</Pressable>
			</Tooltip>
			{/* Interactive elements positioned outside the Pressable to avoid nested-interactive a11y violation */}
			<Box
				xcss={
					getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
						? interactiveOverlayStylesSingleSelect
						: interactiveOverlayStyles
				}
			>
				{showTimeoutOverlay && (
					<FilterOptionTimeout option={option} setIsMouseOver={setIsMouseOver} />
				)}
				{showFilterMenuOverlay && (
					<FilterMenu
						isVisible={isMenuTriggerVisible}
						onClickMenuItem={onClickMenuItem}
						isMenuOpen={isMenuOpen}
						onClick={(event) => {
							event.stopPropagation();
							setIsMenuOpen(!isMenuOpen);
						}}
						onClose={() => setIsMenuOpen(false)}
						onHoverMenuButton={onHoverMenuButton}
						onHoverMenuItem={onHoverMenuItem}
					/>
				)}
			</Box>
		</Box>
	);
};

export default FilterOption;
